/**
 * useBusinessLanguage — sync the i18n UI language to the business preference
 * captured at onboarding (or in Settings → Localization).
 *
 * Initialization order, highest priority last:
 *   1. i18next default            (en, falls through to localStorage / navigator)
 *   2. business.language          (this hook — flips i18n on first load + on changes)
 *   3. user explicit override     (sessionStorage flag set by the language picker)
 *
 * The user-override flag is cleared on logout (see useAuth) so a user logging
 * in to a different business gets that business's default unless they opt out
 * again.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const USER_OVERRIDE_KEY = "adam_lang_user_override";

export function setLanguageOverride(): void {
  // Called by any UI that explicitly changes the user's language preference,
  // so the business-default sync in this hook stays out of the way.
  try {
    sessionStorage.setItem(USER_OVERRIDE_KEY, "1");
  } catch {
    // sessionStorage is unavailable in some embedded contexts — ignore.
  }
}

export function clearLanguageOverride(): void {
  // Logout / business-switch resets the user override so the next session
  // picks up the business default again.
  try {
    sessionStorage.removeItem(USER_OVERRIDE_KEY);
  } catch {
    // ignore
  }
}

function hasUserOverride(): boolean {
  try {
    return sessionStorage.getItem(USER_OVERRIDE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Sync `i18n` to `businessLanguage` whenever it loads or changes, unless the
 * user has explicitly chosen a language this session.
 *
 * Pass `null`/`undefined` while business is loading; the hook is a no-op then.
 */
export function useBusinessLanguage(businessLanguage: string | null | undefined): void {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!businessLanguage) return;
    if (hasUserOverride()) return;
    const current = (i18n.language ?? "").split("-")[0];
    if (current === businessLanguage) return;
    void i18n.changeLanguage(businessLanguage);
  }, [businessLanguage, i18n]);
}
